// ignore-string-externalization
import React from 'react';
import { hexToRgba } from '@mrkt/features/color-conversion';
import styled from 'styled-components';
import { body2FontSize, cta1LetterSpacing, spacer8, spacer16, spacer24, spacer32, screenXsMax, screenLgMin, heading3FontSize, heading3LineHeight, spacer40, heading4FontSize, Type, IconArrowRight } from '@spotify-internal/encore-web';
import { ContentfulImage } from '../../Contentful/ContentfulImage';
import { Box, ImageAspectBox1x1, ButtonTertiaryAsLink, Supertext, Link, withAnimationContext, DELAYS, fadeInAnimation, CUSTOM_IPAD_WIDTH } from '../../ui';
import { formatText, fixHref, withoutProps } from '../../utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var StyledSupertext = styled(Supertext).withConfig({
  displayName: "HeroHeaderModule__StyledSupertext",
  componentId: "m89oyp-0"
})(["padding-bottom:", ";font-size:", ";letter-spacing:", ";"], spacer16, body2FontSize, cta1LetterSpacing);
var Header = styled(Box).withConfig({
  displayName: "HeroHeaderModule__Header",
  componentId: "m89oyp-1"
})(["background-color:", ";color:", ";height:auto;display:flex;&.tallest{height:100%;}@media (min-width:", "){justify-content:center;padding-bottom:", ";}"], function (props) {
  return props.backgroundColor;
}, function (props) {
  return props.textColor;
}, screenLgMin, spacer40);
var Content = styled.div.withConfig({
  displayName: "HeroHeaderModule__Content",
  componentId: "m89oyp-2"
})(["display:flex;flex-flow:row-reverse wrap;justify-content:space-between;width:100%;@media (max-width:", "){flex-direction:column;justify-content:flex-start;padding-bottom:", ";}@media (min-width:", "){max-width:1700px;justify-content:space-evenly;}"], CUSTOM_IPAD_WIDTH, spacer32, screenLgMin);
var ImageContainer = styled(ImageAspectBox1x1).withConfig({
  displayName: "HeroHeaderModule__ImageContainer",
  componentId: "m89oyp-3"
})(["width:40%;display:flex;align-items:center;height:fit-content;align-self:center;max-height:616px;max-width:616px;margin-top:80px;@media (max-width:", "){margin-top:40px;max-height:unset;align-self:start;}@media (max-width:", "){width:100%;margin-bottom:", ";}"], CUSTOM_IPAD_WIDTH, CUSTOM_IPAD_WIDTH, spacer8);
var InnerText = styled.div.withConfig({
  displayName: "HeroHeaderModule__InnerText",
  componentId: "m89oyp-4"
})(["padding-left:0px;width:50%;margin-top:auto;padding-top:80px;@media (max-width:", "){padding-top:40px;}margin-bottom:auto;@media (max-width:", "){width:100%;margin:0px;}@media (min-width:", "){width:40%;}"], CUSTOM_IPAD_WIDTH, CUSTOM_IPAD_WIDTH, screenLgMin); // TODO Remove hardcoding of font-size - there is no 56 in Encore, it is Frodor specific

var Headline = withAnimationContext(styled(Type.h1).attrs({
  variant: Type.heading1,
  weight: Type.bold
}).withConfig({
  displayName: "HeroHeaderModule__Headline",
  componentId: "m89oyp-5"
})(["", ";font-size:clamp(32px,5vw,94px);line-height:clamp(54px,5vw,104px);padding-bottom:", ";max-width:684px;@media (max-width:", "){padding-top:", ";padding-bottom:", ";font-size:48px;line-height:54px;}@media (max-width:", "){text-align:left;}"], fadeInAnimation(DELAYS.SECOND), spacer32, CUSTOM_IPAD_WIDTH, spacer8, spacer24, CUSTOM_IPAD_WIDTH));
var Subtext = withAnimationContext(styled(Type.h4).attrs({
  variant: Type.heading4
}).withConfig({
  displayName: "HeroHeaderModule__Subtext",
  componentId: "m89oyp-6"
})(["", ";padding-bottom:", ";font-size:", ";line-height:", ";@media (max-width:1600px){font-size:", ";line-height:", ";}"], fadeInAnimation(DELAYS.THIRD), spacer32, heading3FontSize, heading3LineHeight, heading4FontSize, heading3FontSize));
var StyledLink = styled(withoutProps(Link, 'textColor')).withConfig({
  displayName: "HeroHeaderModule__StyledLink",
  componentId: "m89oyp-7"
})(["color:", ";padding-top:", ";&&:hover:not(:disabled),&&:focus:not(:disabled){color:", ";}&&:focus:not(:disabled) h1{outline:1px;outline-style:dotted;outline-color:black;}"], function (_ref) {
  var textColor = _ref.textColor;
  return textColor;
}, spacer32, function (props) {
  return hexToRgba(props.textColor || '', 0.75);
});
var FeaturedContentLink = withAnimationContext(styled(withoutProps(ButtonTertiaryAsLink, 'textColor')).withConfig({
  displayName: "HeroHeaderModule__FeaturedContentLink",
  componentId: "m89oyp-8"
})(["", ";padding-left:0px;padding-top:0;padding-bottom:", ";border-radius:0;color:", ";&&:hover:not(:disabled),&&:focus:not(:disabled){color:", ";}@media (max-width:", "){padding-top:0px;padding-bottom:0px;}svg{padding-left:", ";vertical-align:-7px;}"], fadeInAnimation(DELAYS.FOURTH), spacer32, function (_ref2) {
  var textColor = _ref2.textColor;
  return textColor;
}, function (props) {
  return hexToRgba(props.textColor || '', 0.75);
}, screenXsMax, spacer8));

var getHeadline = function getHeadline(ctaText, ctaLink, headline, textColor, trackCat) {
  if (headline) {
    var HeadlineElement = /*#__PURE__*/_jsx(Headline, {
      textColor: textColor,
      "data-testid": "header-module-headline",
      dangerouslySetInnerHTML: {
        __html: formatText(headline)
      }
    });

    if (ctaText && ctaLink) {
      return /*#__PURE__*/_jsx(StyledLink, {
        textColor: textColor,
        href: fixHref(ctaLink),
        track: [trackCat, "CTA Link - ".concat(ctaText)],
        children: HeadlineElement
      });
    }

    return HeadlineElement;
  }

  return /*#__PURE__*/_jsx(_Fragment, {});
};

var TRACK_CAT = 'module-heroHeader';
export var HeaderModule = /*#__PURE__*/React.forwardRef(function (_ref3, ref) {
  var _ref3$headline = _ref3.headline,
      headline = _ref3$headline === void 0 ? '' : _ref3$headline,
      _ref3$subtext = _ref3.subtext,
      subtext = _ref3$subtext === void 0 ? '' : _ref3$subtext,
      _ref3$supertext = _ref3.supertext,
      supertext = _ref3$supertext === void 0 ? '' : _ref3$supertext,
      image = _ref3.image,
      _ref3$ctaLink = _ref3.ctaLink,
      ctaLink = _ref3$ctaLink === void 0 ? '' : _ref3$ctaLink,
      _ref3$ctaText = _ref3.ctaText,
      ctaText = _ref3$ctaText === void 0 ? '' : _ref3$ctaText,
      _ref3$backgroundColor = _ref3.backgroundColor,
      backgroundColor = _ref3$backgroundColor === void 0 ? '' : _ref3$backgroundColor,
      _ref3$textColor = _ref3.textColor,
      textColor = _ref3$textColor === void 0 ? '' : _ref3$textColor;
  return /*#__PURE__*/_jsx(Header, {
    backgroundColor: backgroundColor,
    textColor: textColor,
    ref: ref,
    children: /*#__PURE__*/_jsxs(Content, {
      children: [/*#__PURE__*/_jsx(ImageContainer, {
        children: image && /*#__PURE__*/_jsx(ContentfulImage, {
          asset: image
        })
      }), /*#__PURE__*/_jsxs(InnerText, {
        children: [supertext && /*#__PURE__*/_jsx(StyledSupertext, {
          children: supertext
        }), getHeadline(ctaText, ctaLink, headline, textColor, TRACK_CAT), subtext && /*#__PURE__*/_jsx(Subtext, {
          dangerouslySetInnerHTML: {
            __html: formatText(subtext)
          }
        }), ctaText && ctaLink && /*#__PURE__*/_jsxs(FeaturedContentLink, {
          textColor: textColor,
          href: fixHref(ctaLink),
          track: [TRACK_CAT, "CTA Link - ".concat(ctaText)],
          children: [ctaText, /*#__PURE__*/_jsx(IconArrowRight, {})]
        })]
      })]
    })
  });
});